import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

export default function MeetingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['meeting', id],
    queryFn: () => api.meetings.get(id),
  });

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center">Chargement...</div>;
  }

  const meeting = data?.meeting;
  const agenda = data?.agenda || [];
  const participants = data?.participants || [];
  const documents = data?.documents || [];
  const actionItems = data?.actionItems || [];

  if (!meeting) {
    return <div>Réunion non trouvée</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate(-1)}
            className="text-blue-600 hover:underline mb-2"
          >
            ← Retour
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{meeting.title}</h1>
          <p className="text-gray-600">
            {new Date(meeting.scheduled_at * 1000).toLocaleString('fr-FR')}
          </p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h2 className="text-xl font-semibold mb-4">Ordre du jour</h2>
            <div className="bg-white p-6 rounded-lg shadow">
              {agenda.length > 0 ? (
                <ol className="space-y-3">
                  {agenda
                    .sort((a, b) => a.order - b.order)
                    .map((item) => (
                      <li key={item.id} className="border-b pb-2 last:border-0">
                        <p className="font-medium">{item.title}</p>
                        {item.presenter_user_id && (
                          <p className="text-sm text-gray-600">
                            Présentateur: {item.presenter_user_id.substring(0, 8)}
                          </p>
                        )}
                      </li>
                    ))}
                </ol>
              ) : (
                <p className="text-gray-500">Aucun point à l'ordre du jour</p>
              )}
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-4">Participants</h2>
            <div className="bg-white p-6 rounded-lg shadow">
              {participants.length > 0 ? (
                <div className="space-y-2">
                  {participants.map((p) => (
                    <div key={p.id} className="flex justify-between">
                      <span>{p.user_id.substring(0, 8)}</span>
                      <span className="text-sm text-gray-600">{p.status}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">Aucun participant</p>
              )}
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-4">Documents</h2>
            <div className="bg-white p-6 rounded-lg shadow">
              {documents.length > 0 ? (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <div key={doc.id} className="border-b pb-2 last:border-0">
                      <p className="font-medium">{doc.filename}</p>
                      <p className="text-sm text-gray-600">{doc.context}</p>
                      <span className="text-xs text-gray-500">{doc.status}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">Aucun document</p>
              )}
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-4">Actions</h2>
            <div className="bg-white p-6 rounded-lg shadow">
              {actionItems.length > 0 ? (
                <div className="space-y-3">
                  {actionItems.map((action) => (
                    <div key={action.id} className="border-b pb-2 last:border-0">
                      <p className="font-medium">{action.description}</p>
                      <p className="text-sm text-gray-600">{action.type}</p>
                      <span className="text-xs text-gray-500">{action.status}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">Aucune action</p>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
